/**
 * Shared spending category definitions
 * Used by useProcessSpendData.js and BudgetItemEdit.jsx
 */

// Business-specific category groupings
export const businessGroups = {
	Home: [
		{ name: "Income", categories: ["Erin Paycheck"] },
		{ name: "Food [Main]", categories: ["Takeout", "Groceries", "Dinner", "Food"] },
		{ name: "Food [Aux]", categories: ["Alcohol", "Coffee"] },
		{ name: "Pup", categories: ["Pup", "Otto - Benes", "Otto - Food", "Otto - Poo Bags", "Otto - Meds", "Otto - Other", "Otto - Treats"] },
		{ name: "Transpo", categories: ["Car", "Car Insurance", "Car - Insurance", "Gas", "Uber", "Parking", "Parking Tickets", "Car Rental", "Cruise"] },
		{ name: "Accounting", categories: ["Card Fee", "Negative Interest", "Fees", "Interest"] },
		{ name: "Gifts", categories: ["Gifts - Delaney", "Gifts", "Gifts - Wes", "Gift"] },
		{ name: "House", categories: ["House Goods", "Plants", "Home Goods"] },
		{ name: "Fitness", categories: ["Fitness", "Gym"] },
		{ name: "Medical", categories: ["Medical", "Health - Erin", "Medical - Visits"] },
		{ name: "Beauty", categories: ["Beauty - Makeup", "Beauty - Hair", "Beauty - Skincare", "Beauty"] },
		{ name: "iTunes & Entertainment", categories: ["iTunes", "Entertainment"] },
		{ name: "Clothes", categories: ["Clothes", "Erin - Clothes"] },
		{ name: "Subscriptions", categories: ["Subscriptions", "Subscription", "Business"] },
	],
	Child: [
		{ name: "Medical", categories: ["Baby - Prenatals"] },
		{ name: "Childcare", categories: ["Baby - Childcare"] },
		{ name: "Doctors", categories: ["Parking"] },
	],
	Other: [
		{ name: "Travel", categories: ["Dewey", "Charlotte", "NYC", "SIC", "Beacon", "AZ", "Erica Wedding", "Ayana NYC", "City Summer", "October Trip"] },
		{ name: "Christmas", categories: ["Wes", "Christmas", "HL60"] },
		{ name: "Activities", categories: ["Activities", "Springsteen", "Bruce", "Phillies"] },
		{ name: "Msc", categories: ["Wedding", "Health Insurance"] },
	],
	Apartment: [
		{ name: "Rent", categories: ["Rent"] },
		{ name: "Utilities", categories: ["Electric", "Gas", "Verizon"] },
		{ name: "Insurance", categories: ["Insurance"] },
	],
	NL: [],
	Immerge: [],
	Projects: [{ name: "Projects", categories: ["CC24", "NAPA"] }],
};

/**
 * Get a flat list of category options for dropdown/multi-select
 * Format: { value: "Business:Category", label: "Business > Category" }
 */
export const getCategoryOptions = () => {
	const options = [];

	Object.entries(businessGroups).forEach(([business, groups]) => {
		// Add the grouped categories
		groups.forEach((group) => {
			// Add the group name as an option
			options.push({
				value: `${business}:${group.name}`,
				label: `${business} > ${group.name}`,
				business,
				category: group.name,
				subcategories: group.categories,
			});
		});
	});

	return options;
};

/**
 * Get category options organized by business (for grouped UI)
 * Returns group-level options
 */
export const getCategoryOptionsByBusiness = () => {
	const byBusiness = {};

	Object.entries(businessGroups).forEach(([business, groups]) => {
		if (groups.length === 0) return;

		byBusiness[business] = groups.map((group) => ({
			value: `${business}:${group.name}`,
			label: group.name,
			subcategories: group.categories,
		}));
	});

	return byBusiness;
};

/**
 * Get subcategory options organized by business > group (for detailed UI)
 * Returns the actual spending categories (e.g., "Takeout", "Groceries")
 */
export const getSubcategoryOptionsByBusiness = () => {
	const byBusiness = {};

	Object.entries(businessGroups).forEach(([business, groups]) => {
		if (groups.length === 0) return;

		byBusiness[business] = {};
		groups.forEach((group) => {
			byBusiness[business][group.name] = group.categories.map((cat) => ({
				value: `${business}:${cat}`,
				label: cat,
			}));
		});
	});

	return byBusiness;
};

/**
 * Parse a category reference value (e.g., "Home:Food") into business and category
 */
export const parseCategoryRef = (ref) => {
	if (!ref || typeof ref !== "string") return null;
	const [business, category] = ref.split(":");
	return business && category ? { business, category } : null;
};

/**
 * Get all raw categories that match a category reference
 * Handles both group refs (e.g., "Home:Food" -> ["Takeout", "Groceries", ...])
 * and direct category refs (e.g., "Home:Takeout" -> ["Takeout"])
 */
export const getRawCategoriesForRef = (ref) => {
	const parsed = parseCategoryRef(ref);
	if (!parsed) return [];

	const groups = businessGroups[parsed.business] || [];

	// First check if it's a group name
	const group = groups.find((g) => g.name === parsed.category);
	if (group) {
		return group.categories;
	}

	// Otherwise check if it's a direct subcategory
	for (const g of groups) {
		if (g.categories.includes(parsed.category)) {
			return [parsed.category];
		}
	}

	// Fallback: return as-is (may be a category not in our groups)
	return [parsed.category];
};
